import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingLaborPaysBySelect } from "./ManufacturingLaborPaysBySelect";
import {
  laborRateImportCounts,
  type LaborRateImportRow,
} from "@/lib/manufacturing/laborCompanyRateImport";
import type { LaborPaysBy } from "@/lib/manufacturing/laborPayQuantity";

function money(n: number | null) {
  if (n == null) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function ManufacturingLaborCompanyRateImportReview({
  rows,
  busy,
  onAccept,
  onReject,
  onAcceptAll,
  onPaysBy,
  onPaysByAllSuggested,
  onFinish,
  onBack,
}: {
  rows: LaborRateImportRow[];
  busy: boolean;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAcceptAll: () => void;
  onPaysBy: (id: string, paysBy: LaborPaysBy | "") => void;
  onPaysByAllSuggested: (paysBy: LaborPaysBy | "") => void;
  onFinish: () => void;
  onBack: () => void;
}) {
  const counts = laborRateImportCounts(rows);
  const ready = rows.filter((r) => r.status === "suggested" && r.pays_by).length;
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        <strong>{counts.suggested}</strong> suggested · <strong>{counts.unmatched}</strong> unmatched ·{" "}
        <strong>{counts.rejected}</strong> rejected · <strong>{counts.accepted}</strong> accepted.
        Choose Pays by before accepting — do not assume quantity 1. Finish saves accepted matches and parks
        unmatched and rejected rows (with a rate) so you can match them later.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-[12rem] text-xs text-gray-600 dark:text-zinc-400">
          Set Pays by on suggested
          <ManufacturingLaborPaysBySelect
            value=""
            disabled={busy || counts.suggested === 0}
            onChange={onPaysByAllSuggested}
          />
        </label>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy || ready === 0}
          onClick={onAcceptAll}
        >
          Accept all with Pays by
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onBack}>
          Back
        </button>
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={onFinish}>
          Finish
        </button>
      </div>
      <ul className="max-h-[55vh] divide-y divide-gray-100 overflow-y-auto dark:divide-zinc-800">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="min-w-0">
              <div className="font-mono text-sm text-gray-900 dark:text-zinc-100">
                {row.recipe ? `${row.recipe.code} — ${row.recipe.name}` : row.sku || row.name || "(no SKU)"}
              </div>
              <div className="text-xs text-gray-500 dark:text-zinc-400">
                {money(row.rate_usd)}
                {row.label ? ` · ${row.label}` : ""}
                {row.existing_rate_id ? " · updates existing" : ""}
                {row.status !== "suggested" ? ` · ${row.status}${row.reason ? ` (${row.reason})` : ""}` : ""}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {row.status === "suggested" || row.status === "unmatched" ? (
                <div className="w-[11rem]">
                  <ManufacturingLaborPaysBySelect
                    value={row.pays_by ?? ""}
                    disabled={busy}
                    onChange={(paysBy) => onPaysBy(row.id, paysBy)}
                  />
                </div>
              ) : null}
              {row.status === "suggested" ? (
                <>
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={busy || !row.pays_by}
                    onClick={() => onAccept(row.id)}
                  >
                    Accept
                  </button>
                  <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => onReject(row.id)}>
                    Reject
                  </button>
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
